import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const withEntry = (search, entryId) => {
  const params = new URLSearchParams(search);

  if (entryId == null) {
    params.delete('entry');
  } else {
    params.set('entry', entryId);
  }

  const query = params.toString();
  return query ? `?${query}` : '';
};

const useEntryViewDialog = (entries) => {
  const history = useHistory();
  const { search } = useLocation();

  const openEntryId = new URLSearchParams(search).get('entry');

  // Opening, stepping and closing only change ?entry=, so they keep the reader's place
  const showEntry = useCallback((entryId, method, extraState) => {
    const { pathname, search: current, hash, state } = history.location;

    history[method]({ pathname, search: withEntry(current, entryId), hash, state: { ...state, ...extraState, disableScrollToTop: true } });
  }, [history]);

  const openIndex = openEntryId == null
    ? -1
    : entries.findIndex((entry) => String(entry.id) === String(openEntryId));
  const selectedEntry = openIndex >= 0 ? entries[openIndex] : null;

  const openEntry = useCallback((entry) => showEntry(String(entry.id), 'push', { entryPushed: true }), [showEntry]);

  // Going back instead of replacing keeps one history entry per page, so the back button leaves the page
  const closeEntry = useCallback(() => {
    if (history.location.state?.entryPushed) {
      history.goBack();
    } else {
      showEntry(null, 'replace');
    }
  }, [history, showEntry]);
  const navigate = useCallback((delta) => {
    const next = openIndex + delta;
    if (next >= 0 && next < entries.length) {
      showEntry(String(entries[next].id), 'replace');
    }
  }, [openIndex, entries, showEntry]);

  return {
    openEntry,
    dialogProps: {
      open: !!selectedEntry,
      entry: selectedEntry,
      onClose: closeEntry,
      onPrev: () => navigate(-1),
      onNext: () => navigate(1),
      hasPrev: openIndex > 0,
      hasNext: openIndex >= 0 && openIndex < entries.length - 1,
    },
  };
};

export default useEntryViewDialog;
